import { useState } from 'react';
import { useApp } from '../context/AppContext';
import { authApi } from '../api/auth';
import { LoginRequest, VerifyOTPRequest, RegisterRequest, Tenant } from '../types';

export function useAuth() {
  const { state, setUser, setTenant, setLoading, addNotification } = useApp();
  const [otpSent, setOtpSent] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');

  const sendOTP = async (data: LoginRequest) => {
    try {
      setLoading(true);
      const response = await authApi.sendOTP(data);
      
      if (response.success) {
        setOtpSent(true);
        setPhoneNumber(data.phone);
        addNotification({
          type: 'success',
          title: 'OTP Sent',
          message: 'Please check your phone for the verification code',
        });
      } else {
        addNotification({
          type: 'error',
          title: 'Failed to Send OTP',
          message: response.error || 'Please try again',
        });
      }
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.response?.data?.error || 'Failed to send OTP',
      });
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async (data: VerifyOTPRequest) => {
    try {
      setLoading(true);
      const response = await authApi.verifyOTP(data);
      
      if (response.success && response.data) {
        if (response.data.is_new_user) {
          // New user - need to complete registration
          return { isNewUser: true, phoneNormalized: response.data.phone_normalized };
        } else {
          // Existing user - store JWT token and set user data
          if (response.data.token) {
            localStorage.setItem('auth_token', response.data.token);
          }
          setUser(response.data.user!);
          addNotification({
            type: 'success',
            title: 'Welcome Back',
            message: 'Successfully logged in',
          });
          return { isNewUser: false };
        }
      } else {
        addNotification({
          type: 'error',
          title: 'Invalid OTP',
          message: response.error || 'Please check your code and try again',
        });
        return null;
      }
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.response?.data?.error || 'Failed to verify OTP',
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const register = async (data: RegisterRequest) => {
    try {
      setLoading(true);
      
      // First create tenant
      const tenantResponse = await authApi.createTenant(data.phone);
      if (!tenantResponse.success) {
        throw new Error(tenantResponse.error || 'Failed to create tenant');
      }

      // Then create user
      const userData = {
        ...data,
        tenant_id: (tenantResponse.data as Tenant).id,
      };
      
      const userResponse = await authApi.register(userData);
      if (!userResponse.success || !userResponse.data) {
        throw new Error(userResponse.error || 'Failed to create user');
      }

      // Store JWT token from registration response
      const responseData = userResponse.data as any;
      if (responseData.token) {
        localStorage.setItem('auth_token', responseData.token);
      }

      // Handle both response formats: { user, token } or just User
      const user = responseData.user || responseData;
      setUser(user);
      setTenant(tenantResponse.data as Tenant);
      
      addNotification({
        type: 'success',
        title: 'Welcome to Chat Safe',
        message: 'Your account has been created successfully',
      });

      return true;
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Registration Failed',
        message: error.response?.data?.error || error.message || 'Failed to create account',
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const resetAuth = () => {
    setOtpSent(false);
    setPhoneNumber('');
  };

  return {
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    user: state.user,
    tenant: state.tenant,
    otpSent,
    phoneNumber,
    sendOTP,
    verifyOTP,
    register,
    resetAuth,
  };
}
